import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardAction,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
  NavigationMenuContent,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectGroup,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import Header from "@/components/Header";
import MaterialIcon from "@/components/ui/MaterialIcon";
import EmployeeScoreCard from "@/components/EmployeeScoreCard";
import Container from "@/components/Container";
import Page from "@/components/Page";

const StyleGuidePage: React.FunctionComponent = () => {
  return (
    <Page className="min-h-screen bg-background">
      <Container className="pb-8 space-y-12">
        <div className="space-y-2">
          <Heading level={1} variant="xl" className="text-4xl">Style Guide</Heading>
          <Text variant="lg" className="text-muted-foreground">
            A comprehensive showcase of all UI components and their variants
          </Text>
        </div>

        {/* Colors Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Colors</Heading>

          {/* Neutral Range */}
          <div>
            <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Neutral</Heading>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-3">
              {[
                { name: "0", className: "bg-neutral-0" },
                { name: "100", className: "bg-neutral-100" },
                { name: "200", className: "bg-neutral-200" },
                { name: "300", className: "bg-neutral-300" },
                { name: "400", className: "bg-neutral-400" },
                { name: "500", className: "bg-neutral-500" },
                { name: "600", className: "bg-neutral-600" },
                { name: "700", className: "bg-neutral-700" },
                { name: "800", className: "bg-neutral-800" },
                { name: "900", className: "bg-neutral-900" },
                { name: "1000", className: "bg-neutral-1000" },
              ].map((color) => (
                <div key={color.name} className="space-y-1.5">
                  <div
                    className={`${color.className} aspect-square rounded-lg border border-border`}
                  />
                  <Text variant="xs" className="font-mono text-muted-foreground">{color.name}</Text>
                </div>
              ))}
            </div>
          </div>

          {/* Highlight Range */}
          <div>
            <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Highlight</Heading>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-3">
              {[
                { name: "500", className: "bg-highlight-500" },
                { name: "600", className: "bg-highlight-600" },
              ].map((color) => (
                <div key={color.name} className="space-y-1.5">
                  <div
                    className={`${color.className} aspect-square rounded-lg border border-border`}
                  />
                  <Text variant="xs" className="font-mono text-muted-foreground">{color.name}</Text>
                </div>
              ))}
            </div>
          </div>

          {/* Status Colors */}
          <div>
            <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Status Colors</Heading>
            <div className="space-y-6">
              {/* Base status swatches */}
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
                {[
                  { name: "Success", className: "bg-success" },
                  { name: "Success Foreground", className: "bg-success-foreground" },
                  { name: "Success Muted", className: "bg-success-muted" },
                  { name: "Warning", className: "bg-warning" },
                  { name: "Warning Foreground", className: "bg-warning-foreground" },
                  { name: "Warning Muted", className: "bg-warning-muted" },
                  { name: "Destructive", className: "bg-destructive" },
                  { name: "Destructive Foreground", className: "bg-destructive-foreground" },
                  { name: "Destructive Muted", className: "bg-destructive-muted" },
                ].map((color) => (
                  <div key={color.name} className="space-y-1.5">
                    <div
                      className={`${color.className} aspect-square rounded-lg border border-border`}
                    />
                    <Text variant="xs" className="font-mono text-muted-foreground">{color.name}</Text>
                  </div>
                ))}
              </div>

              {/* Composed status examples */}
              <div>
                <Heading level={4} variant="xs" className="text-sm text-muted-foreground mb-3">
                  Composed usage — muted background + muted-foreground text
                </Heading>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                  <div className="rounded-lg bg-success-muted px-4 py-3">
                    <Text variant="sm" className="text-success-muted-foreground font-semibold">Success</Text>
                    <Text variant="xs" className="text-success-muted-foreground mt-0.5">
                      bg-success-muted / text-success-muted-foreground
                    </Text>
                  </div>
                  <div className="rounded-lg bg-warning-muted px-4 py-3">
                    <Text variant="sm" className="text-warning-muted-foreground font-semibold">Warning</Text>
                    <Text variant="xs" className="text-warning-muted-foreground mt-0.5">
                      bg-warning-muted / text-warning-muted-foreground
                    </Text>
                  </div>
                  <div className="rounded-lg bg-destructive-muted px-4 py-3">
                    <Text variant="sm" className="text-destructive-muted-foreground font-semibold">Destructive</Text>
                    <Text variant="xs" className="text-destructive-muted-foreground mt-0.5">
                      bg-destructive-muted / text-destructive-muted-foreground
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Core Palette */}
          <div>
            <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Core Palette</Heading>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-3">
              {[
                { name: "Green 500", className: "bg-green-500" },
                { name: "Blue 600", className: "bg-blue-600" },
                { name: "Yellow 400", className: "bg-yellow-400" },
                { name: "Amber 500", className: "bg-amber-500" },
                { name: "Purple 500", className: "bg-purple-500" },
                { name: "Coral 500", className: "bg-coral-500" },
              ].map((color) => (
                <div key={color.name} className="space-y-1.5">
                  <div
                    className={`${color.className} aspect-square rounded-lg border border-border`}
                  />
                  <Text variant="xs" className="font-mono text-muted-foreground">{color.name}</Text>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Heading Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Heading</Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Variants</Heading>
              <div className="space-y-4">
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">XL Variant (Wide Special Gothic)</Text>
                  <Heading variant="xl" level={1}>The quick brown fox jumps</Heading>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">LG Variant (Wide Special Gothic)</Text>
                  <Heading variant="lg" level={2}>The quick brown fox jumps</Heading>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">MD Variant (Normal Width)</Text>
                  <Heading variant="md" level={3}>The quick brown fox jumps</Heading>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">SM Variant (Wide Special Gothic)</Text>
                  <Heading variant="sm" level={4}>The quick brown fox jumps</Heading>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">XS Variant (Mono Font)</Text>
                  <Heading variant="xs" level={5}>The quick brown fox jumps</Heading>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Text Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Text</Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Variants</Heading>
              <div className="space-y-4">
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">XL Variant</Text>
                  <Text variant="xl">
                    The quick brown fox jumps over the lazy dog. This is an extra large text variant with standard width Special Gothic font.
                  </Text>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">LG Variant</Text>
                  <Text variant="lg">
                    The quick brown fox jumps over the lazy dog. This is a large text variant with standard width Special Gothic font.
                  </Text>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">MD Variant (Default)</Text>
                  <Text variant="md">
                    The quick brown fox jumps over the lazy dog. This is a medium text variant with standard width Special Gothic font.
                  </Text>
                </div>
                <div>
                  <Text variant="sm" className="text-muted-foreground mb-2">SM Variant</Text>
                  <Text variant="sm">
                    The quick brown fox jumps over the lazy dog. This is a small text variant with standard width Special Gothic font.
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Header Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Header</Heading>
          <div className="space-y-4">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Signed In State</Heading>
              <div className="border rounded-lg overflow-hidden">
                <Header
                  user={{
                    id: "1",
                    email: "user@example.com",
                    workspace: { id: "1", name: "Example Workspace" },
                  }}
                  onSignOut={() => alert("Sign out clicked")}
                  isSigningOut={false}
                  embedded
                />
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Signed Out State</Heading>
              <div className="border rounded-lg overflow-hidden">
                <Header user={null} embedded />
              </div>
            </div>
          </div>
        </section>

        {/* Button Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Button</Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Variants</Heading>
              <div className="flex flex-wrap gap-3">
                <Button variant="highlight">Highlight</Button>
                <Button variant="default">Default</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="link">Link</Button>
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Sizes</Heading>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm">Small</Button>
                <Button size="default">Default</Button>
                <Button size="lg">Large</Button>
                <Button size="icon">
                  <MaterialIcon icon="add" size={16} />
                </Button>
                <Button size="icon-sm">
                  <MaterialIcon icon="add" size={14} />
                </Button>
                <Button size="icon-lg">
                  <MaterialIcon icon="add" size={20} />
                </Button>
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">With Icons</Heading>
              <div className="flex flex-wrap gap-3">
                <Button>
                  <MaterialIcon icon="mail" size={16} />
                  Login with Email
                </Button>
                <Button variant="outline">
                  <MaterialIcon icon="search" size={16} />
                  Search
                </Button>
                <Button variant="destructive">
                  <MaterialIcon icon="delete" size={16} />
                  Delete
                </Button>
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">States</Heading>
              <div className="flex flex-wrap gap-3">
                <Button disabled>Disabled</Button>
                <Button variant="outline" disabled>
                  Disabled Outline
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Card Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Card</Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Basic Card</CardTitle>
                <CardDescription>
                  This is a simple card with a header and content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Text variant="sm">
                  Cards are used to group related content together. They can
                  contain headers, content, and footers.
                </Text>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Card with Action</CardTitle>
                <CardDescription>Includes an action button</CardDescription>
                <CardAction>
                  <Button size="sm" variant="outline">
                    Edit
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent>
                <Text variant="sm">
                  The CardAction component allows you to add action buttons to
                  the header.
                </Text>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Card with Footer</CardTitle>
                <CardDescription>Includes footer actions</CardDescription>
              </CardHeader>
              <CardContent>
                <Text variant="sm">
                  Cards can have footers for additional actions or information.
                </Text>
              </CardContent>
              <CardFooter className="gap-2">
                <Button size="sm" variant="outline">
                  Cancel
                </Button>
                <Button size="sm">Save</Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader className="border-b">
                <CardTitle>Bordered Header</CardTitle>
                <CardDescription>Header with bottom border</CardDescription>
              </CardHeader>
              <CardContent>
                <Text variant="sm">
                  Add the border-b class to CardHeader to separate it from the
                  content.
                </Text>
              </CardContent>
              <CardFooter className="border-t">
                <Button size="sm">Action</Button>
              </CardFooter>
            </Card>
          </div>
        </section>

        {/* Input Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Input</Heading>
          <div className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="text-input">Text Input</Label>
              <Input id="text-input" type="text" placeholder="Enter text..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-input">Email Input</Label>
              <Input
                id="email-input"
                type="email"
                placeholder="name@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password-input">Password Input</Label>
              <Input
                id="password-input"
                type="password"
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="disabled-input">Disabled Input</Label>
              <Input
                id="disabled-input"
                type="text"
                placeholder="Disabled"
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invalid-input">Invalid Input</Label>
              <Input
                id="invalid-input"
                type="text"
                placeholder="Invalid"
                aria-invalid="true"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="file-input">File Input</Label>
              <Input id="file-input" type="file" />
            </div>
          </div>
        </section>

        {/* Navigation Menu Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">
            Navigation Menu
          </Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Simple Links</Heading>
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Home
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      About
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Contact
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">With Dropdown</Heading>
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Home
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger>Products</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid gap-3 p-4 w-[400px]">
                        <NavigationMenuLink>
                          <Text as="div" className="font-medium">Product 1</Text>
                          <Text variant="sm" className="text-muted-foreground">
                            Description of product 1
                          </Text>
                        </NavigationMenuLink>
                        <NavigationMenuLink>
                          <Text as="div" className="font-medium">Product 2</Text>
                          <Text variant="sm" className="text-muted-foreground">
                            Description of product 2
                          </Text>
                        </NavigationMenuLink>
                        <NavigationMenuLink>
                          <Text as="div" className="font-medium">Product 3</Text>
                          <Text variant="sm" className="text-muted-foreground">
                            Description of product 3
                          </Text>
                        </NavigationMenuLink>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                      Contact
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>
        </section>

        {/* Popover Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Popover</Heading>
          <div className="flex flex-wrap gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">Open Popover</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="space-y-2">
                  <Heading level={4} variant="xs" className="normal-case font-sans tracking-normal">Popover Title</Heading>
                  <Text variant="sm" className="text-muted-foreground">
                    This is a popover with some content inside.
                  </Text>
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">With Actions</Button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Heading level={4} variant="xs" className="normal-case font-sans tracking-normal">Account Settings</Heading>
                    <Text variant="sm" className="text-muted-foreground">
                      Manage your account preferences.
                    </Text>
                  </div>
                <div className="space-y-2">
                  <Button size="sm" variant="outline" className="w-full justify-start">
                    <MaterialIcon icon="person" size={16} />
                    Profile
                  </Button>
                  <Button size="sm" variant="outline" className="w-full justify-start">
                    <MaterialIcon icon="settings" size={16} />
                    Settings
                  </Button>
                  <Button size="sm" variant="outline" className="w-full justify-start">
                    <MaterialIcon icon="logout" size={16} />
                    Logout
                  </Button>
                </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </section>

        {/* Radio Group Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Radio Group</Heading>
          <div className="space-y-4 max-w-md">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Default</Heading>
              <RadioGroup defaultValue="option-1">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-1" id="option-1" />
                  <Label htmlFor="option-1">Option 1</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-2" id="option-2" />
                  <Label htmlFor="option-2">Option 2</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-3" id="option-3" />
                  <Label htmlFor="option-3">Option 3</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">With Descriptions</Heading>
              <RadioGroup defaultValue="comfortable">
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="comfortable" id="comfortable" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="comfortable">Comfortable</Label>
                    <Text variant="sm" className="text-muted-foreground">
                      A comfortable spacing option for better readability
                    </Text>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="compact" id="compact" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="compact">Compact</Label>
                    <Text variant="sm" className="text-muted-foreground">
                      A compact spacing option to fit more content
                    </Text>
                  </div>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Disabled</Heading>
              <RadioGroup defaultValue="option-1" disabled>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-1" id="disabled-1" />
                  <Label htmlFor="disabled-1">Disabled Option 1</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-2" id="disabled-2" />
                  <Label htmlFor="disabled-2">Disabled Option 2</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </section>

        {/* Select Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Select</Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            <div className="space-y-2">
              <Label>Default Select</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="option1">Option 1</SelectItem>
                  <SelectItem value="option2">Option 2</SelectItem>
                  <SelectItem value="option3">Option 3</SelectItem>
                  <SelectItem value="option4">Option 4</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Small Select</Label>
              <Select>
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Select size small" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm1">Small Option 1</SelectItem>
                  <SelectItem value="sm2">Small Option 2</SelectItem>
                  <SelectItem value="sm3">Small Option 3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>With Groups</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select from groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Fruits</SelectLabel>
                    <SelectItem value="apple">Apple</SelectItem>
                    <SelectItem value="banana">Banana</SelectItem>
                    <SelectItem value="orange">Orange</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Vegetables</SelectLabel>
                    <SelectItem value="carrot">Carrot</SelectItem>
                    <SelectItem value="broccoli">Broccoli</SelectItem>
                    <SelectItem value="spinach">Spinach</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Disabled Select</Label>
              <Select disabled>
                <SelectTrigger>
                  <SelectValue placeholder="Disabled" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="option1">Option 1</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>With Icons</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select with icons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="home">
                    <MaterialIcon icon="home" size={16} />
                    Home
                  </SelectItem>
                  <SelectItem value="user">
                    <MaterialIcon icon="person" size={16} />
                    User
                  </SelectItem>
                  <SelectItem value="settings">
                    <MaterialIcon icon="settings" size={16} />
                    Settings
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Skeleton Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Skeleton</Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Loading States</Heading>
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Card Skeleton</Heading>
              <Card>
                <CardHeader>
                  <Skeleton className="h-6 w-1/3 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </CardContent>
              </Card>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Avatar Skeleton</Heading>
              <div className="flex items-center space-x-4">
                <Skeleton className="size-12 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Empty Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Empty</Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">With Icon, Title &amp; Description</Heading>
              <Empty className="border bg-muted/30">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <MaterialIcon icon="inbox" size={24} />
                  </EmptyMedia>
                  <EmptyTitle>No items yet</EmptyTitle>
                  <EmptyDescription>
                    Items will appear here once they have been added to the system.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">With Action Button</Heading>
              <Empty className="border bg-muted/30">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <MaterialIcon icon="link" size={24} />
                  </EmptyMedia>
                  <EmptyTitle>Connect your support platform</EmptyTitle>
                  <EmptyDescription>
                    Link your customer support tools to start syncing conversations and agent interactions.
                  </EmptyDescription>
                  <Button variant="outline" className="mt-2">
                    <MaterialIcon icon="add" size={16} />
                    Connect Platform
                  </Button>
                </EmptyHeader>
              </Empty>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Minimal (Title Only)</Heading>
              <Empty className="border bg-muted/30">
                <EmptyHeader>
                  <EmptyTitle>Nothing to show</EmptyTitle>
                  <EmptyDescription>
                    Check back later for updates.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Tabs</Heading>
          <div className="space-y-6">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Default Tabs</Heading>
              <Tabs defaultValue="tab1">
                <TabsList>
                  <TabsTrigger value="tab1">Tab 1</TabsTrigger>
                  <TabsTrigger value="tab2">Tab 2</TabsTrigger>
                  <TabsTrigger value="tab3">Tab 3</TabsTrigger>
                </TabsList>
                <TabsContent value="tab1" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Tab 1 Content</CardTitle>
                      <CardDescription>
                        This is the content for the first tab
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Text variant="sm">
                        Tabs allow you to organize content into different
                        sections that users can navigate between.
                      </Text>
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent value="tab2" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Tab 2 Content</CardTitle>
                      <CardDescription>
                        This is the content for the second tab
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Text variant="sm">
                        Each tab can contain completely different content,
                        making them perfect for settings pages or dashboards.
                      </Text>
                    </CardContent>
                  </Card>
                </TabsContent>
                <TabsContent value="tab3" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Tab 3 Content</CardTitle>
                      <CardDescription>
                        This is the content for the third tab
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Text variant="sm">
                        You can have as many tabs as you need to organize your
                        content effectively.
                      </Text>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Tabs with Icons</Heading>
              <Tabs defaultValue="home">
                <TabsList>
                  <TabsTrigger value="home">
                    <MaterialIcon icon="home" size={16} />
                    Home
                  </TabsTrigger>
                  <TabsTrigger value="user">
                    <MaterialIcon icon="person" size={16} />
                    Profile
                  </TabsTrigger>
                  <TabsTrigger value="settings">
                    <MaterialIcon icon="settings" size={16} />
                    Settings
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="home">
                  <Text variant="sm" className="text-muted-foreground py-4">
                    Home tab content with icon
                  </Text>
                </TabsContent>
                <TabsContent value="user">
                  <Text variant="sm" className="text-muted-foreground py-4">
                    Profile tab content with icon
                  </Text>
                </TabsContent>
                <TabsContent value="settings">
                  <Text variant="sm" className="text-muted-foreground py-4">
                    Settings tab content with icon
                  </Text>
                </TabsContent>
              </Tabs>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Disabled Tab</Heading>
              <Tabs defaultValue="enabled1">
                <TabsList>
                  <TabsTrigger value="enabled1">Enabled 1</TabsTrigger>
                  <TabsTrigger value="disabled" disabled>
                    Disabled
                  </TabsTrigger>
                  <TabsTrigger value="enabled2">Enabled 2</TabsTrigger>
                </TabsList>
                <TabsContent value="enabled1">
                  <Text variant="sm" className="text-muted-foreground py-4">
                    First enabled tab
                  </Text>
                </TabsContent>
                <TabsContent value="enabled2">
                  <Text variant="sm" className="text-muted-foreground py-4">
                    Second enabled tab
                  </Text>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </section>

        {/* Checkbox Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Checkbox</Heading>
          <div className="space-y-4 max-w-md">
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Default</Heading>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="checkbox1" />
                  <Label htmlFor="checkbox1">Accept terms and conditions</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="checkbox2" defaultChecked />
                  <Label htmlFor="checkbox2">
                    Send me promotional emails
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="checkbox3" />
                  <Label htmlFor="checkbox3">Remember my preferences</Label>
                </div>
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Disabled</Heading>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="disabled1" disabled />
                  <Label htmlFor="disabled1">Disabled unchecked</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="disabled2" disabled defaultChecked />
                  <Label htmlFor="disabled2">Disabled checked</Label>
                </div>
              </div>
            </div>
            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">With Descriptions</Heading>
              <div className="space-y-4">
                <div className="flex items-start space-x-2">
                  <Checkbox id="desc1" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="desc1">Marketing emails</Label>
                    <Text variant="sm" className="text-muted-foreground">
                      Receive emails about new products, features, and more
                    </Text>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox id="desc2" className="mt-1" defaultChecked />
                  <div className="space-y-1">
                    <Label htmlFor="desc2">Security alerts</Label>
                    <Text variant="sm" className="text-muted-foreground">
                      Get notified when there&apos;s unusual activity on your
                      account
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Employee Score Card Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Employee Score Card</Heading>
          <div className="space-y-6">
            <div>
              <Text variant="sm" className="text-muted-foreground mb-4">
                Score cards display an employee&apos;s name, email, and score with color-coded status.
                Colors are determined by score thresholds using semantic tokens:
                <Text as="span" className="text-success font-medium"> success</Text> (≥ 90),
                <Text as="span" className="text-warning font-medium"> warning</Text> (≥ 75), and
                <Text as="span" className="text-destructive font-medium"> destructive</Text> (&lt; 75).
              </Text>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">
                High Score
                <Text as="span" variant="sm" className="font-normal text-muted-foreground ml-2">
                  (≥ 90 — <code className="text-success">success</code> token)
                </Text>
              </Heading>
              <div className="max-w-lg">
                <EmployeeScoreCard
                  employeeScore={{
                    employeeId: "demo-1",
                    name: "Alice Johnson",
                    email: "alice.johnson@example.com",
                    score: 95,
                  }}
                  averagePoints={70}
                />
              </div>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">
                Medium Score
                <Text as="span" variant="sm" className="font-normal text-muted-foreground ml-2">
                  (≥ 75 — <code className="text-warning">warning</code> token)
                </Text>
              </Heading>
              <div className="max-w-lg">
                <EmployeeScoreCard
                  employeeScore={{
                    employeeId: "demo-2",
                    name: "Bob Martinez",
                    email: "bob.martinez@example.com",
                    score: 82,
                  }}
                  averagePoints={70}
                />
              </div>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">
                Low Score
                <Text as="span" variant="sm" className="font-normal text-muted-foreground ml-2">
                  (&lt; 75 — <code className="text-destructive">destructive</code> token)
                </Text>
              </Heading>
              <div className="max-w-lg">
                <EmployeeScoreCard
                  employeeScore={{
                    employeeId: "demo-3",
                    name: "Carol Davis",
                    email: "carol.davis@example.com",
                    score: 58,
                  }}
                  averagePoints={70}
                />
              </div>
            </div>

            <div>
              <Heading level={3} variant="xs" className="text-lg normal-case font-medium font-sans tracking-normal mb-3">Color Token Reference</Heading>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg">
                <div className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="size-8 rounded bg-success-muted flex items-center justify-center">
                    <Text as="span" variant="sm" className="text-success-muted-foreground font-bold">95</Text>
                  </div>
                  <div>
                    <Text variant="sm" className="font-medium">Success</Text>
                    <Text variant="xs" className="text-muted-foreground font-mono">success-muted</Text>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="size-8 rounded bg-warning-muted flex items-center justify-center">
                    <Text as="span" variant="sm" className="text-warning-muted-foreground font-bold">82</Text>
                  </div>
                  <div>
                    <Text variant="sm" className="font-medium">Warning</Text>
                    <Text variant="xs" className="text-muted-foreground font-mono">warning-muted</Text>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="size-8 rounded bg-destructive-muted flex items-center justify-center">
                    <Text as="span" variant="sm" className="text-destructive-muted-foreground font-bold">58</Text>
                  </div>
                  <div>
                    <Text variant="sm" className="font-medium">Destructive</Text>
                    <Text variant="xs" className="text-muted-foreground font-mono">destructive-muted</Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Label Section */}
        <section className="space-y-4">
          <Heading level={2} variant="sm" className="border-b pb-2">Label</Heading>
          <div className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label>Simple Label</Label>
              <Input placeholder="Input with label" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="with-id">Label with htmlFor</Label>
              <Input id="with-id" placeholder="Linked to label" />
            </div>
            <div className="space-y-2">
              <Label>
                <MaterialIcon icon="mail" size={16} />
                Label with Icon
              </Label>
              <Input type="email" placeholder="email@example.com" />
            </div>
          </div>
        </section>
      </Container>
    </Page>
  );
};

export default StyleGuidePage;
